import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import PeptideCalculatorPro from "../components/PeptideCalculatorPro";
import NutritionCalculator from "../components/NutritionCalculator";
import CalcHistoryPanel from "../components/CalcHistoryPanel";
import { Calculator as CalcIcon, Utensils, Calculator } from "lucide-react";
import PageHero from "../components/PageHero";

const TABS = [
  { key: "peptide",   label: "Peptide Calculator",   Icon: CalcIcon,
    subtitle: "BAC water + mg + dose → exact syringe units." },
  { key: "nutrition", label: "Nutrition Calculator", Icon: Utensils,
    subtitle: "BMR + TDEE → daily calorie & macro targets you can save to your profile." },
];

/**
 * Parent page for the Calculators tab. Renders a tab switcher between
 * Peptide and Nutrition calculators, each with its own saved-runs catalog.
 * Deep-link with `?tab=nutrition` from the nav.
 */
export default function Calculators() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get("tab") === "nutrition" ? "nutrition" : "peptide";

  const [tab, setTab] = useState(initialTab);
  const [preset, setPreset] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const switchTab = (nextKey) => {
    setTab(nextKey);
    setPreset(null); // clear any pending load when switching tabs
    const next = new URLSearchParams(location.search);
    if (nextKey === "peptide") next.delete("tab"); else next.set("tab", nextKey);
    navigate({ pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" }, { replace: true });
  };

  const active = TABS.find((t) => t.key === tab) || TABS[0];

  return (
    <AppLayout>
      <PageHero
        eyebrow="Heavy Lifting"
        title="Calculators"
        subtitle={active.subtitle}
        icon={<Calculator className="w-8 h-8 text-[#0D9488]" />}
        gradient
      />

      {/* Tab switcher */}
      <div className="flex border-b border-slate-200 mb-6" role="tablist" data-testid="calculators-tabs">
        {TABS.map((t) => {
          const isActive = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => switchTab(t.key)}
              data-testid={`calc-tab-${t.key}`}
              className={[
                "flex items-center gap-2 px-5 py-3 font-mono-num text-[11px] uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors",
                isActive
                  ? "border-[#0D9488] text-[#0D9488]"
                  : "border-transparent text-slate-500 hover:text-slate-900",
              ].join(" ")}
            >
              <t.Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Body: calculator + catalog sidebar */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6" data-testid={`calc-body-${tab}`}>
        <div className="min-w-0">
          {tab === "peptide" && (
            <PeptideCalculatorPro
              preset={preset}
              onSaved={() => setRefresh((n) => n + 1)}
            />
          )}
          {tab === "nutrition" && (
            <NutritionCalculator
              preset={preset}
              onSaved={() => setRefresh((n) => n + 1)}
            />
          )}
        </div>
        <aside className="xl:sticky xl:top-6 xl:self-start">
          <CalcHistoryPanel
            calcType={tab}
            onLoad={(e) => setPreset(e)}
            refreshToken={refresh}
            renderSummary={(e) => {
              const out = e.outputs || {};
              if (e.calc_type === "nutrition") {
                return (
                  <span>
                    {out.calories ?? "?"} kcal · {out.protein_g ?? "?"} g P · {out.carbs_g ?? "?"} g C · {out.fat_g ?? "?"} g F
                  </span>
                );
              }
              const inp = e.inputs || {};
              return (
                <span>
                  {inp.amount_mg ?? "?"} mg / {inp.bac_ml ?? "?"} mL · {inp.dose_value ?? "?"} {inp.dose_unit || ""} → {Number(out.draw_units || 0).toFixed(1)} u
                </span>
              );
            }}
          />
        </aside>
      </div>
    </AppLayout>
  );
}
